/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from 'react'
import { Flex, Stack } from '@mantine/core'
import dayjs from 'dayjs'
import { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table'
import { getUsersByProvince } from 'src/apis/chartAPI'
import CustomClearInput from 'src/components/CustomComponents/CustomClearInput'
import CustomSelect from 'src/components/CustomComponents/CustomSelect'
import TableComponent from 'src/components/TableComponent'
import actionClasses from 'src/components/TableComponent/TableComponent.module.scss'
import { FieldType, UserData } from 'src/interfaces'
import { color, formatDate } from 'src/variable/constant'
import { useModal } from 'src/context/ModalContext'
import { LinkOutlined } from '@mui/icons-material'
import { formatTimeNumber } from 'src/utils/utils'

interface ProvinceDetailProps {
	name: string
	columns: MRT_ColumnDef[]
	fields: FieldType[]
	handleDetail: (row: any) => void
	[key: string]: any
}

function ProvinceDetail({
	name,
	columns,
	fields,
	handleDetail,
}: ProvinceDetailProps) {
	const { openError } = useModal()

	const [data, setData] = useState<UserData[]>([])
	const [pagination, setPagination] = useState<MRT_PaginationState>({
		pageIndex: 1,
		pageSize: 10,
	})
	const [value, setValue] = useState({
		field: fields[0].value,
		value: null,
	})
	const [total, setTotal] = useState<number>(0)
	const [loading, setLoading] = useState<boolean>(false)

	const handleGetData = useCallback(async () => {
		let arr: UserData[] = []
		let count: number = 0
		let filter = {}
		const provinceName = name ? decodeURIComponent(name) : ''
		setLoading(true)
		try {
			// filter = filterUserPageList(value)
					// 
			filter = value.value != null ? (filter = { [value.field]: value.value }) : (filter = {})
			const res = await getUsersByProvince({
				fields: ['$all'],
				page: pagination.pageIndex,
				limit: pagination.pageSize,
				where: { 
					...filter,
					province: provinceName 
				},
			})
			const rows =
				res?.data?.results?.objects?.rows ||
				res?.data?.results?.object?.rows ||
				res?.data?.results?.objects ||
				res?.data?.results?.object ||
				[]
			arr = rows?.map((item, index) => ({
				no: index + 1 + pagination.pageSize * (pagination.pageIndex - 1),
				...item,
				created_at: dayjs(item?.created_at).format(formatDate),
				deleted_at: item?.deleted_at
					? dayjs(item?.deleted_at).format(formatDate)
					: null,
				last_login_at: formatTimeNumber(Number(item?.last_login_at))
			}))
			count =
				res?.data?.results?.objects?.count ||
				res?.data?.results?.object?.count ||
				rows?.length ||
				0
		} catch (error) {
			openError(error)
		} finally {
			setData(arr)
			setTotal(count)

			setTimeout(() => {
				setLoading(false)
			}, 500)
		}
	}, [name, JSON.stringify(pagination), JSON.stringify(value)])

	const handleSetValue = (e, key) => {
		if (key === 'field' && value.value === '') {
			setValue((pre) => ({ ...pre, [key]: e, value: null }))
		} else {
			setValue((pre) => ({ ...pre, [key]: e }))
		}
	}

	const handleChangePage = useCallback((value: number) => {
		setPagination((pre) => ({
			...pre,
			pageIndex: value,
		}))
	}, [])

	const _renderCustomActions = useCallback((row) => {
		const { share_link } = row?.original
		return (
			<Stack sx={{ order: 10 }}>
				<a
					href={share_link ? share_link : '#'}
					target="_blank"
					rel="noreferrer noopener"
					className={`${actionClasses.actionButton} ${actionClasses.primaryAction}`}
				>
					<LinkOutlined fontSize="small" />
				</a>
			</Stack>
		)
	}, [])

	useEffect(() => {
		handleGetData()
	}, [JSON.stringify(pagination)])

	useEffect(() => {
		let timer
		if (value.value !== null) {
			timer = setTimeout(() => {
				if (pagination.pageIndex === 1) {
					handleGetData()
				} else handleChangePage(1)
			}, 500)
		}
		return () => clearTimeout(timer)
	}, [JSON.stringify(value)])

	return (
		<Stack sx={{ width: '100%' }}>
			<Flex
				gap={8}
				justify="space-between"
				sx={{ flexWrap: 'wrap' }}
				align="center"
			>
				<Flex gap={8} sx={{ flexWrap: 'wrap' }}>
					<CustomSelect
						value={value.field}
						data={fields}
						onChange={(e) => {
							handleSetValue(e, 'field')
						}}
					/>
					<CustomClearInput
						value={value.value}
						onChange={(e) => handleSetValue(e, 'value')}
						placeholder={'Search'}
					/>
				</Flex>
			</Flex>

			<Flex
				gap={8}
				align="center"
				sx={{ fontSize: 20, fontWeight: 600, color: color.CmsPrimary }}
			>
				<div>Total: {total || ''}</div>
			</Flex>

			<TableComponent
				columns={columns}
				total={total}
				data={data}
				pagination={pagination}
				loading={loading}
				handleChangePage={handleChangePage}
				handleDetail={handleDetail}
				_renderCustomActions={_renderCustomActions}
			/>
		</Stack>
	)
}

export default React.memo(ProvinceDetail)
